import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { motion } from 'framer-motion';
import { Trash2, Search, Mail, Phone, Calendar } from 'lucide-react';
import { toast } from 'sonner';

export default function LeadsManager() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API_URL}/leads?limit=500`);
      setLeads(res.data);
    } catch (err) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      await axios.delete(`${API_URL}/leads/${id}`);
      toast.success("Lead deleted");
      fetchLeads();
    } catch (err) {
      toast.error("Failed to delete lead");
    }
  };

  const filteredLeads = leads.filter(l => 
    l.name.toLowerCase().includes(search.toLowerCase()) || 
    l.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 flex flex-col h-full">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Leads Manager</h2>
          <p className="text-neutral-400 text-sm">Review contact form submissions.</p>
        </div>
        <div className="relative w-full sm:w-64">
           <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
           <input 
             type="text" 
             placeholder="Search leads..." 
             value={search}
             onChange={e => setSearch(e.target.value)}
             className="w-full bg-neutral-900 border border-neutral-800 rounded-xl pl-10 pr-4 py-2 text-white focus:outline-none focus:border-amber-500 transition"
           />
        </div>
      </div>

      <div className="flex-1 bg-neutral-900/50 backdrop-blur-md border border-neutral-800 rounded-2xl overflow-hidden flex flex-col">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="overflow-x-auto flex-1">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-neutral-900 border-b border-neutral-800 text-neutral-400 text-sm">
                  <th className="py-4 px-6 font-medium">Name</th>
                  <th className="py-4 px-6 font-medium">Contact</th>
                  <th className="py-4 px-6 font-medium">Service Info</th>
                  <th className="py-4 px-6 font-medium">Message</th>
                  <th className="py-4 px-6 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead, idx) => (
                  <motion.tr 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    key={lead.id} 
                    className="border-b border-neutral-800/50 hover:bg-neutral-800/20 transition"
                  >
                    <td className="py-4 px-6 align-top">
                      <div className="font-medium text-white">{lead.name}</div>
                      <div className="text-xs text-neutral-500 flex items-center gap-1 mt-1">
                        <Calendar size={12} />
                        {new Date(lead.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="py-4 px-6 align-top text-sm text-neutral-300">
                      <div className="flex items-center gap-2 mb-1">
                        <Mail size={14} className="text-amber-500" /> {lead.email}
                      </div>
                      {lead.phone && (
                        <div className="flex items-center gap-2">
                          <Phone size={14} className="text-amber-500" /> {lead.phone}
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-6 align-top text-sm text-neutral-300">
                      {lead.company && <div className="mb-1"><span className="text-neutral-500">Company:</span> {lead.company}</div>}
                      {lead.service && <div><span className="text-neutral-500">Service:</span> {lead.service}</div>}
                    </td>
                    <td className="py-4 px-6 align-top text-sm text-neutral-400">
                      <div className="max-w-xs break-words line-clamp-3 hover:line-clamp-none transition-all">
                        {lead.message}
                      </div>
                    </td>
                    <td className="py-4 px-6 align-top text-right">
                      <button 
                        onClick={() => handleDelete(lead.id)}
                        className="text-neutral-500 hover:text-red-500 p-2 rounded-lg hover:bg-red-500/10 transition inline-flex"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </motion.tr>
                ))}
                {filteredLeads.length === 0 && (
                  <tr>
                    <td colSpan="5" className="py-12 text-center text-neutral-500">
                      No leads found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
